import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  Alert,
  LinearProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@mui/material';
import {
  Refresh,
  Computer,
  Smartphone,
  Tablet,
  Delete,
  PlayArrow,
  Stop,
  Storage,
  NetworkCheck,
  DeviceHub
} from '@mui/icons-material';
import { useI18n } from '../i18n/i18n';
import electronAPI from '../services/electronAPI';

const ServerAdmin = () => {
  const { t } = useI18n();
  const [devices, setDevices] = useState([]);
  const [serverStats, setServerStats] = useState({
    uptime: 0,
    connectedDevices: 0,
    totalSyncs: 0,
    memoryUsage: 0
  });
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [revokeDialog, setRevokeDialog] = useState({ open: false, device: null });

  const loadServerData = async () => {
    setLoading(true);
    try {
      const devicesData = await electronAPI.getDevices();
      setDevices(devicesData || []);

      // Simulate server stats (in real app, this would come from the server)
      setServerStats({
        uptime: Date.now() - (24 * 60 * 60 * 1000), // 24 hours ago
        connectedDevices: devicesData.filter(d => d.status === 'online').length,
        totalSyncs: Math.floor(Math.random() * 1000),
        memoryUsage: Math.random() * 100
      });

    } catch (error) {
      console.error('Failed to load server data:', error);
      setMessage({
        type: 'error',
        text: 'Failed to load server data'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadServerData();
    const interval = setInterval(loadServerData, 30000); // Refresh every 30 seconds
    return () => clearInterval(interval);
  }, []);

  const openRevokeDialog = (device) => {
    setRevokeDialog({ open: true, device });
  };

  const closeRevokeDialog = () => {
    setRevokeDialog({ open: false, device: null });
  };

  const revokeDevice = async () => {
    if (!revokeDialog.device) return;

    try {
      await electronAPI.revokeDevice(revokeDialog.device.id);
      setMessage({
        type: 'success',
        text: `Device ${revokeDialog.device.name} revoked successfully`
      });
      await loadServerData();
      closeRevokeDialog();
    } catch (error) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to revoke device'
      });
    }
  };

  const restartServer = async () => {
    // This would trigger a server restart in production
    setMessage({
      type: 'info',
      text: 'Server restart functionality would be implemented here'
    });
  };

  const getDeviceIcon = (type) => {
    switch (type) {
      case 'mobile': return <Smartphone />;
      case 'tablet': return <Tablet />;
      default: return <Computer />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'online': return 'success';
      case 'offline': return 'default';
      default: return 'default';
    }
  };

  const formatUptime = (uptimeMs) => {
    const hours = Math.floor(uptimeMs / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    if (days > 0) return `${days}d ${hours % 24}h`;
    return `${hours}h`;
  };

  const onlineDevices = devices.filter(d => d.status === 'online');
  const offlineDevices = devices.filter(d => d.status === 'offline');

  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        🖥️ {t('serverAdmin.title', 'Server Administration')}
      </Typography>

      {message.text && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>
          {message.text}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Server Statistics */}
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <NetworkCheck sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
              <Typography variant="h4" color="primary.main">
                {serverStats.connectedDevices}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Connected Devices
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <DeviceHub sx={{ fontSize: 40, color: 'secondary.main', mb: 1 }} />
              <Typography variant="h4" color="secondary.main">
                {serverStats.totalSyncs}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Total Syncs
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <PlayArrow sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
              <Typography variant="h4" color="success.main">
                {formatUptime(serverStats.uptime)}
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Uptime
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <Storage sx={{ fontSize: 40, color: 'warning.main', mb: 1 }} />
              <Typography variant="h4" color="warning.main">
                {Math.round(serverStats.memoryUsage)}%
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Memory Usage
              </Typography>
              <LinearProgress 
                variant="determinate" 
                value={serverStats.memoryUsage} 
                sx={{ mt: 1 }}
                color={serverStats.memoryUsage > 80 ? 'error' : 'primary'}
              />
            </CardContent>
          </Card>
        </Grid>

        {/* Online Devices */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  📱 Online Devices ({onlineDevices.length})
                </Typography>
                <Button startIcon={<Refresh />} onClick={loadServerData}>
                  Refresh
                </Button>
              </Box>

              {onlineDevices.length === 0 ? (
                <Typography color="textSecondary" textAlign="center" py={3}>
                  No devices currently online
                </Typography>
              ) : (
                <List>
                  {onlineDevices.map((device) => (
                    <ListItem key={device.id} divider>
                      <ListItemIcon>
                        {getDeviceIcon(device.type)}
                      </ListItemIcon>
                      <ListItemText
                        primary={device.name}
                        secondary={
                          <Box>
                            <Typography variant="caption" display="block">
                              {device.type} • {device.ip_address || 'Unknown IP'}
                            </Typography>
                            <Typography variant="caption" display="block">
                              Last seen: {device.last_seen ? new Date(device.last_seen).toLocaleString() : 'Never'}
                            </Typography>
                          </Box>
                        }
                      />
                      <IconButton
                        color="error"
                        onClick={() => openRevokeDialog(device)}
                        title="Revoke Device"
                      >
                        <Delete />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* All Devices Table */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                All Registered Devices ({devices.length})
              </Typography>

              <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Device</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Last Seen</TableCell>
                      <TableCell>Type</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {devices.map((device) => (
                      <TableRow key={device.id}>
                        <TableCell>
                          <Box display="flex" alignItems="center">
                            {getDeviceIcon(device.type)}
                            <Box ml={1}>
                              <Typography variant="body2">
                                {device.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {device.id?.slice(0, 8)}...
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={device.status}
                            color={getStatusColor(device.status)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {device.last_seen 
                            ? new Date(device.last_seen).toLocaleString()
                            : 'Never'
                          }
                        </TableCell>
                        <TableCell>
                          <Chip label={device.type} size="small" variant="outlined" />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Server Actions */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Server Management
              </Typography>
              <Box display="flex" gap={2}>
                <Button
                  variant="contained"
                  startIcon={<Refresh />}
                  onClick={loadServerData}
                >
                  Refresh Data
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Stop />}
                  onClick={restartServer}
                  color="warning"
                >
                  Restart Server
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<Storage />}
                  onClick={() => electronAPI.createBackup()}
                >
                  Create Backup
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Revoke Device Dialog */}
      <Dialog
        open={revokeDialog.open}
        onClose={closeRevokeDialog}
      >
        <DialogTitle>
          Revoke Device Access
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to revoke access for device:
          </Typography>
          <Typography variant="h6" sx={{ mt: 1 }}>
            {revokeDialog.device?.name}
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {revokeDialog.device?.type} • {revokeDialog.device?.id}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRevokeDialog}>Cancel</Button>
          <Button onClick={revokeDevice} color="error" variant="contained">
            Revoke Access
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ServerAdmin;